import { inject, Injectable, Injector } from '@angular/core';
import { LanguageService } from '../../../commons/services/languages.service';
import { MobileService } from '../../../commons/services/mobile.service';
import { NavigationService } from '../../../commons/services/navigation.service';
import { ELocalStorageKeys, EQueryStringParams } from '../../../commons/stores/local-storage.service';
import { StoreService } from '../../../commons/stores/store.service';
import { TvLoginStore } from './app.store';

@Injectable()
export class InitService {
  readonly tvLoginStore = inject(TvLoginStore);

  constructor(
    private storeService: StoreService,
    private injector: Injector,
    private languageService: LanguageService,
    private _mobileService: MobileService,
    private readonly _navigationService: NavigationService
  ) {}

  checkRedirects(): Promise<boolean> {
    this.prepareStorage();

    return this.checkSSOLogin();
  }

  private putUrlParamsToStorage(): void {
    Object.keys(EQueryStringParams).forEach((key) => {
      const value = this.getUrlParameterByName(EQueryStringParams[key]);

      if (value) {
        this.tvLoginStore.putUrlParameterToStorage(EQueryStringParams[key], value);
      }
    });

    // if email is in the url query params (the mobile flow) or in the localStorage -> put it to the store for reactivity
    const email = this.getUrlParameterByName(ELocalStorageKeys.email) || this.tvLoginStore.getEmailFromLocalStorage();

    if (email) {
      this.tvLoginStore.setEmail(email);
    }
  }

  private async prepareStorage(): Promise<void> {
    this.tvLoginStore.clearUrlParamsInLocalStorage();
    this.putUrlParamsToStorage();

    const languageIdFromMobile = this.getUrlParameterByName('lang');

    if (languageIdFromMobile) {
      const languageId = parseInt(languageIdFromMobile, 10);

      await this.languageService.getSystemLanguages$();
      this.languageService.setSelectedSystemLanguageById(languageId);
    }

    // if (this.tvLoginStore.getUrlParameterIsMobileApp()) { TODO: Looks it's actual for all platforms, not only mobile
    // if the spa is opened from mobile app and email is present in store (from url) -> do not proceed client alias logic for further redirects
    /* TODO: In the case with mobile + email login - we should redirect to monolith with both: client alias and email.
    And expect that monolith will return it back.
    */
    //  if (this.tvLoginStore.email()) return;

    const clientAlias = this.tvLoginStore.getUrlParameterClientAlias();

    if (!clientAlias) {
      // if clientAlias is not in the url query params, try to get it from local storage
      const savedAlias = this.tvLoginStore.getLoginAlias();

      if (savedAlias) {
        // if clientAlias is in local storage, put it to query params - for redirect mobile to client
        this.tvLoginStore.setUrlParameterClientAlias(savedAlias);
      }
    }
    //}
  }

  private checkSSOLogin(): Promise<boolean> {
    const navigationService: NavigationService = this.injector.get(NavigationService);

    if (!navigationService.urlIsNotForTermsAndConditions() || !this._navigationService.isNotErrorRoute()) {
      // if we are on error route or going to Terms, we should let the app continue loading
      return Promise.resolve(true);
    }

    const alias = this.storeService.getLoginAlias();

    if (alias) {
      const redirectAfterLogin = this._navigationService.isRedirectAfterLogin(location.href);
      const needsMobileRedirect = this._navigationService.needsMobileRedirect(redirectAfterLogin);

      if (needsMobileRedirect) {
        const mobileRedirectUrl = this._mobileService.getMobileRedirectUrl(alias);
        this._navigationService.redirectTo(mobileRedirectUrl);
        return new Promise<boolean>(() => {}); // return empty promise to stop further execution
      }

      return navigationService.goToClient(alias);
    }

    const clientAlias = navigationService.getClientAliasFromHash(location.hash);
    const returnUrl = this.getUrlParameterByName(EQueryStringParams.returnUrl);

    if (clientAlias && !returnUrl) {
      /*
      This is a case when user somehow navigated to /:clientName route without returnUrl.
      https://stable-api.trueview.ecosure.com/api/identity/v1_0/#/lynnyk/login
      From bookmark, for example.

      In this case, we should redirect user to monolith with clientAlias.
      */
      navigationService.goToMonolithWithClientAlias(clientAlias);
      return new Promise<boolean>(() => {});
    }

    if (clientAlias) {
      navigationService.goToClient(clientAlias);
    }

    return Promise.resolve(true);
  }

  private getUrlParameterByName(name: string, url?: string) {
    const targetUrl = url ? new URL(url) : new URL(window.location.href);

    return targetUrl.searchParams.get(name);
  }
}
